import { Component, OnInit, OnDestroy, inject, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { HttpClient } from '@angular/common/http';

// PrimeNG imports
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { CardModule } from 'primeng/card';
import { ChipModule } from 'primeng/chip';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TabsModule } from 'primeng/tabs';
import { DividerModule } from 'primeng/divider';
import { ActionRecorderService} from 'src/app/services/action-recorder.service';
import { ToastService } from 'src/app/toast/toast.service';
import { TestRecorderViewModel } from '../../viewmodels/test-recorder.viewmodel';
import { FluidModule } from 'primeng/fluid';
import { ScenarioService } from '../../../scenario/services/scenario.service';
import { StepsService } from '../../../steps/services/steps.service';
import { StepPlaceholderService } from 'src/app/services/step-placeholder.service';
import { ScreenMasterService } from '../../../screen-master/services/screen-master.service';
import { SkeletonService } from '../../../../services/skeleton.service';
import { Screen, MatchRule, ScreenIdentifiers } from '../../../../shared/models/screen.interface';
import { TextareaModule } from 'primeng/textarea';
import { SelectModule } from 'primeng/select';
import { AuthService } from '../../../../auth/login/services/auth.service';

@Component({
  selector: 'app-test-recorder',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    InputTextModule,
    TableModule,
    CardModule,
    ChipModule,
    TagModule,
    TooltipModule,
    FluidModule,
    ConfirmDialogModule,
    TabsModule,
    DividerModule,
    TextareaModule,
    SelectModule
  ],
   providers: [ ConfirmationService,ToastService],
  templateUrl: './test-recorder.component.html',
  styleUrls: ['./test-recorder.component.scss']
})
export class TestRecorderComponent implements OnInit, OnDestroy {
  public viewModel!: TestRecorderViewModel;
  
  // UI state for tab switching
  activeTabIndex: number = 0;
  isRecordingFallback: boolean = false;
  
  // Static options for template
  ruleTypeOptions = [
    { label: 'Page Title Contains', value: 'title_contains' },
    { label: 'URL Contains', value: 'url_contains' },
    { label: 'Element Exists (automation-id)', value: 'element_exists' },
    { label: 'Panel Heading Contains', value: 'heading_contains' },
    { label: 'Breadcrumb Contains', value: 'breadcrumb_contains' }
  ];

  private http = inject(HttpClient);
  private toastService = inject(ToastService);
  private confirmationService = inject(ConfirmationService);
  private actionRecorder = inject(ActionRecorderService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private ngZone = inject(NgZone);
  private scenarioService = inject(ScenarioService);
  private stepsService = inject(StepsService);
  private stepPlaceholderService = inject(StepPlaceholderService);
  private screenMasterService = inject(ScreenMasterService);
  private authService = inject(AuthService);
  private skeletonService = inject(SkeletonService);

  ngOnInit(): void {
    // Initialize viewModel with screenMasterService
    this.viewModel = new TestRecorderViewModel(
      this.http,
      this.toastService,
      this.confirmationService,
      this.actionRecorder,
      this.router,
      this.route,
      this.ngZone,
      this.scenarioService,
      this.stepsService,
      this.stepPlaceholderService,
      this.screenMasterService,
      this.authService,
      this.skeletonService
    );
    
    // Check for mode from query params
    this.route.queryParams.subscribe(params => {
      if (params['mode'] === 'screen-capture') {
        this.viewModel.mode = 'screen-capture';
        this.activeTabIndex = 1; // Switch to Start Screen Capture tab
      } else {
        this.viewModel.mode = 'test-recorder';
        this.activeTabIndex = 0; // Default to Test Recorder tab
      }
    });
    
    // Check for screen editing from navigation state
    const navigation = this.router.getCurrentNavigation();
    if (navigation?.extras?.state?.['screen']) {
      const screen = navigation.extras.state['screen'] as Screen;
      this.viewModel.screenId = screen.screenId || screen.id || '';
      this.viewModel.screenName = screen.name || screen.displayName || '';
      this.viewModel.screenDescription = screen.description || '';
      this.viewModel.matchRules = screen.matchRules || [];
      this.viewModel.minMatch = screen.minMatch || 2;
      if (screen.fallbackSteps) {
        this.viewModel.liveSteps = screen.fallbackSteps;
      }
    }
    
    this.viewModel.init();
  }

  ngOnDestroy(): void {
    this.viewModel.destroy();
  }

  getMultiValueTooltip(): string {
    if (!this.viewModel.tableInfo?.multiValueColumns) return '';
    
    return this.viewModel.tableInfo.multiValueColumns
      .map(col => `${col.name}: ${col.valueCount} values`)
      .join(', ');
  }

  getOperatorTooltip(operator: string): string {
    const tooltips: Record<string, string> = {
      'equals': 'Exact match (default) - Cell value must equal expected value',
      'contains': 'Partial match - Cell value must contain expected value',
      'notEquals': 'Negative match - Cell value must NOT equal expected value',
      'notContains': 'Negative partial match - Cell value must NOT contain expected value',
      'startsWith': 'Prefix match - Cell value must start with expected value',
      'endsWith': 'Suffix match - Cell value must end with expected value',
      'greaterThan': 'Numeric comparison - Cell value must be greater than expected value',
      'lessThan': 'Numeric comparison - Cell value must be less than expected value',
      'isEmpty': 'Empty check - Cell must be empty (no expected value needed)',
      'isNotEmpty': 'Non-empty check - Cell must have content (no expected value needed)'
    };
    return tooltips[operator] || '';
  }
}
